// === SAA:UI:TRUST-CARD ===
// USP sidebar card (eyebrow "Waarom NU'91 & DFD", title, reasons). Content is per-brand
// config (texts.sidebar); the card renders nothing when the brand has no trustItems,
// so brands without the block are untouched. showPhoto (default off) puts the
// advisor photo on top of this card instead of the advisor card.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@sluitstraat/core';
import { CheckCircle, FirstAid, Handshake, Heartbeat, LockSimple, Medal, Scales, ShieldCheck, Tag } from 'phosphor-react';
import type { IconProps } from 'phosphor-react';
import { commonStyles } from '../styles/commonStyles';
import { getSidebarTexts, resolveSidebarText } from '../config/sidebarContent';
import { AdvisorPhoto } from './AdvisorPhoto';

// Trust icons must not duplicate a product icon rendered on the same page (the
// product icon is meaningful, the trust icon decorative — the trust side yields).
const TRUST_ICONS: Record<string, React.ComponentType<IconProps>> = {
  firstaid: FirstAid,
  handshake: Handshake,
  medal: Medal,
  heartbeat: Heartbeat,
  tag: Tag,
  scales: Scales,
  shield: ShieldCheck,
  lock: LockSimple,
  check: CheckCircle,
};

export const TrustCard: React.FC<{ showPhoto?: boolean }> = ({ showPhoto = false }) => {
  const { t, i18n } = useTranslation();
  const { config } = useConfig();
  const sidebar = getSidebarTexts(config);
  const items = sidebar.trustItems ?? [];

  if (items.length === 0) return null;

  return (
    <div style={commonStyles.helpCard}>
      {showPhoto && <AdvisorPhoto />}
      <p style={commonStyles.eyebrowLabel}>
        {resolveSidebarText(t, i18n, sidebar.trustEyebrowKey, 'sidebar.trust.eyebrow')}
      </p>
      <h3 style={commonStyles.helpTitle}>
        {resolveSidebarText(t, i18n, sidebar.trustTitleKey, 'sidebar.trust.title')}
      </h3>
      <ul style={commonStyles.trustList}>
        {items.map((item, idx) => {
          const Icon = TRUST_ICONS[item.icon ?? ''] ?? CheckCircle;
          const n = idx + 1;
          return (
            <li key={idx} style={commonStyles.trustItem}>
              <span style={commonStyles.trustIconTile}>
                <Icon size={18} weight="duotone" aria-hidden="true" />
              </span>
              <div>
                <h4 style={commonStyles.trustItemTitle}>
                  {resolveSidebarText(t, i18n, item.titleKey, `sidebar.trust.item${n}Title`)}
                </h4>
                <p style={commonStyles.trustItemText}>
                  {resolveSidebarText(t, i18n, item.textKey, `sidebar.trust.item${n}Text`)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TrustCard;
